import { useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { getCurrentUserInfo } from "../api/auth";
import type { UserInfoResponse } from "../api/auth";
import type { Configuration } from "../api/configuration";
import { getAccountIsPersonal, getTerminalToken } from "../lib/auth";
import { useAppSelector } from "../store/hooks";

// `GET /user/info` на САМИЯ терминал (общия акаунт X), докато няма влязъл
// служител. Не пипа Redux/auth — гейтът остава на ПИН екрана. Оттук идват
// името на заведението (CodeKeypad) и досегашната тема на терминала, която
// UserConfigurationSync пренася еднократно на компютър без записана тема:
// общият акаунт е потребител като всеки друг и има свой ред в org
// `user_configuration`, който `PUT /configuration/user` с терминалния токен
// редактира.
//
// Кешът се изпразва при всяка смяна на самоличността (main.tsx →
// removeQueries), затова след изход на служител заявката тръгва наново.
export const SHARED_ACCOUNT_INFO_KEY = ["shared-account-info"] as const;

/**
 * ПИН състояние на общ терминал: терминалът е логнат, служител няма и
 * заявките тръгват с ТЕРМИНАЛНИЯ токен. Последното е важно: по време на
 * възстановяване на служител (useEmployeeSessionRestore) Redux вече носи
 * неговия токен, а `personal` още не е true — тогава заявка оттук би
 * записала служителя под ключа на общия акаунт и би дублирала 401 пътя.
 */
export const useIsSharedTerminalWithoutEmployee = (): boolean => {
  const personal = useAppSelector((s) => s.auth.personal);
  const reduxToken = useAppSelector((s) => s.auth.token);
  const terminalToken = getTerminalToken();
  return (
    getAccountIsPersonal() === false &&
    personal !== true &&
    !!terminalToken &&
    (reduxToken === null || reduxToken === terminalToken)
  );
};

export const useSharedAccountInfo = () => {
  const enabled = useIsSharedTerminalWithoutEmployee();
  return useQuery({
    queryKey: SHARED_ACCOUNT_INFO_KEY,
    queryFn: () => getCurrentUserInfo(),
    staleTime: Infinity,
    retry: false,
    enabled,
  });
};

/** Слива записана лична настройка на общия акаунт в кеша (след успешен PUT). */
export const patchSharedAccountConfiguration = (
  queryClient: QueryClient,
  patch: Partial<Configuration>,
) => {
  queryClient.setQueryData<UserInfoResponse>(SHARED_ACCOUNT_INFO_KEY, (old) =>
    old
      ? { ...old, configuration: { ...old.configuration, ...patch } }
      : old,
  );
};
